'use client';

import { VStack } from '@cbhq/cds-web/layout';
import { memo, useState, useCallback, useEffect } from 'react';
import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import { TwoColumnLayout } from '@/components/common/TwoColumnLayout';
import { DEFAULT_CARD_SPACER } from '@/constants/layout';
import { ExploreSummary } from '@/components/Explore/ExploreSummary';
import { ExploreAboutCard } from '@/components/Explore/ExploreAboutCard';
import { ExploreList } from '@/components/Explore/ExploreList';
import { ExploreHeader } from '@/components/Explore/ExploreHeader';
import { TokenizedEquitiesList } from '@/features/tokenized-equity/components/explore/TokenizedEquitiesList';
import { TokenizedEquitiesSummary } from '@/features/tokenized-equity/components/explore/TokenizedEquitiesSummary';
import { TokenizedEquitiesTopMovers } from '@/features/tokenized-equity/components/explore/TokenizedEquitiesTopMovers';
import { TokenizedEquitiesAbout } from '@/features/tokenized-equity/components/explore/TokenizedEquitiesAbout';
import { PoolDetailsLayout } from '@/components/Explore/PoolDetails';

// In the Tokenized Equities app there is no Pools tab — always open on equities.
const isTokenizedEquitiesApp = process.env.NEXT_PUBLIC_APP_MODE === 'tokenized-equities';

export const ExploreLayout = memo(function ExploreLayout() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const tabFromUrl = searchParams.get('tab');

  const [activeTab, setActiveTab] = useState<'pools' | 'tokenized-equities'>(
    isTokenizedEquitiesApp || tabFromUrl === 'tokenized-equities' ? 'tokenized-equities' : 'pools',
  );

  // In TE mode the ?pool= param is never valid — pools UI doesn't exist.
  const selectedPoolId =
    isTokenizedEquitiesApp || tabFromUrl === 'tokenized-equities' ? null : searchParams.get('pool');

  // Sync tab state with URL when it changes.
  // In the Tokenized Equities app, always stay on the equities tab.
  useEffect(() => {
    if (tabFromUrl === 'tokenized-equities') {
      setActiveTab('tokenized-equities');
    } else if (tabFromUrl === 'pools' || tabFromUrl === null) {
      setActiveTab(isTokenizedEquitiesApp ? 'tokenized-equities' : 'pools');
    }
  }, [tabFromUrl]);

  // Handle tab change and update URL
  const handleTabChange = useCallback(
    (tab: 'pools' | 'tokenized-equities') => {
      setActiveTab(tab);
      // Update URL and create new history entry for proper back button support
      const url = tab === 'pools' ? '/' : '/?tab=tokenized-equities';
      router.push(url, { scroll: false });
    },
    [router],
  );

  const isPoolsTab = activeTab === 'pools';

  const handlePoolSelect = useCallback(
    (poolId: string) => {
      const params = new URLSearchParams(searchParams.toString());
      params.set('pool', poolId);
      router.push(`${pathname}?${params.toString()}`, { scroll: false });
    },
    [router, searchParams, pathname],
  );

  const handleBackToExplore = useCallback(() => {
    const params = new URLSearchParams(searchParams.toString());
    params.delete('pool');
    const qs = params.toString();
    router.push(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
  }, [router, searchParams, pathname]);

  const handleEquitySelect = useCallback(
    (tokenAddress: string) => {
      router.push(`/explore/equity/${tokenAddress}`);
    },
    [router],
  );

  // Show Pool Details view when a pool is selected
  if (selectedPoolId) {
    return <PoolDetailsLayout poolId={selectedPoolId} onBack={handleBackToExplore} />;
  }

  const mainContent = (
    <VStack gap={DEFAULT_CARD_SPACER}>
      {isPoolsTab ? (
        <ExploreList onPoolSelect={handlePoolSelect} />
      ) : (
        <TokenizedEquitiesList onEquitySelect={handleEquitySelect} />
      )}
    </VStack>
  );

  const sidebarContent = (
    <VStack gap={DEFAULT_CARD_SPACER}>
      {isPoolsTab ? (
        <>
          <ExploreSummary />
          <ExploreAboutCard />
        </>
      ) : (
        <>
          <TokenizedEquitiesSummary />
          <TokenizedEquitiesTopMovers />
          <TokenizedEquitiesAbout />
        </>
      )}
    </VStack>
  );

  return (
    <TwoColumnLayout
      header={<ExploreHeader activeTab={activeTab} onTabChange={handleTabChange} />}
      main={mainContent}
      sidebar={sidebarContent}
    />
  );
});
